import { useGetPerformanceAffiliatesReportQuery } from "api/TenantSlug/Report/hooks";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell";
import { last30DaysCreatedAtStr } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { PayoutResult, PerformanceReportDataSourceEnum, PerformanceReportViewEnum } from "types/enums";

import { COMPACT_DAYS } from "../shared/constants";
import { SearchableTable } from "../shared/SearchableTable";

type Series = App.Http.Responses.Report.PerformanceReportSeriesResponse;

export const BestAffiliates: React.FC = () => {
    const { isAdmin } = useUser();
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tR } = usePaldockTranslation("ui.report.columns");

    const q = useGetPerformanceAffiliatesReportQuery(
        {
            page: {
                number: 1,
                size: COMPACT_DAYS,
            },
            filter: {
                filter: {
                    payout_result: {
                        in: [PayoutResult.Approved],
                    },
                    datetime_at: { btw: last30DaysCreatedAtStr },
                },
                sort: "-count",
                include: "owner",
            },
            view: isAdmin() ? PerformanceReportViewEnum.TenantAdmin : PerformanceReportViewEnum.AffiliatePartner,
            source: PerformanceReportDataSourceEnum.ReportPayoutPerformances,
        },
        {
            staleTime: 5000,
        }
    );

    const rows = q.data?.series ?? [];

    return (
        <SearchableTable<Series>
            rows={rows}
            loading={q.isLoading}
            rowKey={(r) => `${r?.owner?.id ?? r?.owner?.nickname ?? "unknown"}-${r?.datetime_at ?? ""}`}
            searchKeys={[
                (r) => r?.owner?.nickname ?? "",
                (r) => String(r?.click ?? ""),
                (r) => String(r?.lead ?? ""),
                (r) => String(r?.count ?? ""),
                (r) => r?.revenue?.formatted_amount ?? String(r?.revenue?.amount ?? ""),
                (r) => r?.cost?.formatted_amount ?? String(r?.cost?.amount ?? ""),
                (r) => r?.profit?.formatted_amount ?? String(r?.profit?.amount ?? ""),
            ]}
            allowSearch={false}
            allowSuffix={false}
            columns={[
                {
                    title: tG("name"),
                    dataIndex: ["owner", "nickname"],
                    render: (_: unknown, r: Series) => <EntityLinkCell.Owner entity={r?.owner} mode="drawer" />,
                },
                { title: tR("clicks"), dataIndex: "click" },
                { title: tR("leads"), dataIndex: "lead" },
                { title: tR("transactions"), dataIndex: "count" },
                {
                    title: tR("revenue"),
                    dataIndex: "revenue",
                    render: (_: string, r: Series) => <MoneyCell money={r.revenue} />,
                },
                {
                    title: tR("cost"),
                    dataIndex: "cost",
                    render: (_: string, r: Series) => <MoneyCell money={r.cost} />,
                },
                {
                    title: tR("profit"),
                    dataIndex: "profit",
                    render: (_: string, r: Series) => <MoneyCell money={r.profit} />,
                },
            ]}
            maxRows={COMPACT_DAYS}
            compact
        />
    );
};
